import React, { useState, useRef } from 'react';
import { Card, TextField, IconButton, InputAdornment } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from "utils/toast";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "../../components/BasicLayout";

// Password utility for validation schema
import PasswordUtility from '../../../passwordutility';

// Images
import bgImage from "assets/images/loginbackground.jpg";
import mastercardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";
import pciLogo from "assets/images/pcilogo.png";
import botimLogo from "assets/images/botim.png";
import version from "../../../../version";
import useApi from "../../../../components/Utils/api";

function ResetWithOtp() {
  const api = useApi();
  const { state } = useLocation();
  const navigate = useNavigate();

  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  // Use the validation schema from PasswordUtility
  const { register, handleSubmit, reset, formState: { errors }, trigger, watch, control, setValue } = useForm({
    mode: "onChange",
    resolver: yupResolver(PasswordUtility.getPasswordValidationSchema()), // Use schema from PasswordUtility
    defaultValues: {
      username: state?.username || "",
      password: "",
      otp: "",
    },
  });

  // 4-digit OTP entered as individual boxes, mirrored into the "otp" form field
  const [otpDigits, setOtpDigits] = useState(["", "", "", ""]);
  const otpInputRefs = useRef([]);

  const handleOtpChange = (index, e) => {
    const digit = e.target.value.replace(/[^0-9]/g, "").slice(-1);
    const next = [...otpDigits];
    next[index] = digit;
    setOtpDigits(next);
    setValue("otp", next.join(""), { shouldValidate: true });
    if (digit && index < 3) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (index, e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 4 - index);
    if (!pasted) return;
    const next = [...otpDigits];
    pasted.split("").forEach((digit, i) => {
      next[index + i] = digit;
    });
    setOtpDigits(next);
    setValue("otp", next.join(""), { shouldValidate: true });
    otpInputRefs.current[Math.min(index + pasted.length, 3)]?.focus();
  };

  // Submit form handler
  const formSubmit = () => {
    handleSubmit(formHandler)();
  }

  // Handle form submission to the API
  const formHandler = (form) => {
    if (state === null) {
      toast.error("Session Expired! Please initiate a reset request first.");
      navigate("/forgot-password");
      return;
    }
    const data = {
      username: form.username,
      password: form.password,
      otp: form.otp,
      user_type: "Merchant",
    }

    // API call to reset password
    api.post("auth/admin/reset-password", data)
      .then(response => {
        const message = response.data.body?.message || response.data.message;
        if (response.data.statusCodeValue === 200) {
          toast.success(message || "Password reset successfully.");
          navigate("/login");
        } else if (message) {
          toast.error(message, { id: "RESET_PASSWORD_ERROR" });
        }
      })
      .catch(error => {
        const message = error.response?.data?.body?.message || error.response?.data?.message;
        toast.error(message || "Failed to reset password. Please try again.");
      })
  }

  return (
    <BasicLayout image={bgImage} showBackground hideFooter>
      <MDBox display="flex" alignItems="center" justifyContent="center" mb={4}>
        <MDBox component="img" src="/login_page_logo.png" alt="Logo" sx={{ height: "56px" }} />
      </MDBox>
      <Card sx={{ minHeight: "370px", width: "calc(100% + 20px)", display: "flex", flexDirection: "column" }}>
        <MDBox pt={4} pb={1} px={3} textAlign="left">
          <MDTypography variant="h5" fontWeight="bold" color="dark" sx={{ letterSpacing: "0.5px" }}>
            Reset Password
          </MDTypography>
          {/* <MDTypography display="block" variant="button" color="text" fontWeight="regular" mt={1}>
            You will receive an email in maximum 60 seconds on your registered email address.
          </MDTypography> */}
        </MDBox>
        <MDBox
          pt={1}
          pb={3}
          px={3}
          sx={{ flex: 1, display: "flex", flexDirection: "column" }}
        >
          <FormProvider {...{ register, handleSubmit, reset, formState: { errors }, trigger, watch, control }}>
            <MDBox sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
              <MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="Username"
                    name="username"
                    error={Boolean(errors.username)} // Retain error state styling (optional)
                    helperText={errors.username ? errors.username.message : ''} // Show error message if there is an error
                    fullWidth
                    {...register("username")}
                    disabled
                    focused
                  />
                </MDBox>
                <MDBox mb={2} style={{ position: 'relative' }}>
                  {/* Password Input Field with Eye Icon and Info Icon on Right */}
                  <MDInput
                    type={showPassword ? "text" : "password"} // Toggle between text and password
                    label="New Password"
                    name="password"
                    error={Boolean(errors.password)} // Enable error state styling for password field
                    helperText={errors.password ? errors.password.message : ''} // Show error message below the field
                    fullWidth
                    {...register("password")}
                    required
                    InputProps={{
                      style: { position: 'relative', color: 'inherit' },
                      // Place both Eye and Info Icons on the right end
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={togglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDTypography variant="caption" fontWeight="regular" color="text" sx={{ display: "block", mb: 1, textAlign: "left" }}>
                    OTP
                  </MDTypography>
                  <MDBox display="flex" justifyContent="center" gap={1.5}>
                    {otpDigits.map((digit, index) => (
                      <TextField
                        key={index}
                        type="password"
                        inputRef={(el) => (otpInputRefs.current[index] = el)}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        onPaste={(e) => handleOtpPaste(index, e)}
                        error={Boolean(errors.otp)}
                        inputProps={{
                          maxLength: 1,
                          inputMode: "numeric",
                          pattern: "[0-9]*",
                          "aria-label": `OTP digit ${index + 1}`,
                          style: { textAlign: "center", fontSize: "1.1rem", padding: "8px 0" },
                        }}
                        sx={{ width: 40 }}
                      />
                    ))}
                  </MDBox>
                  {errors.otp && (
                    <MDTypography variant="caption" color="error" sx={{ display: "block", mt: 0.5, textAlign: "center" }}>
                      {errors.otp.message}
                    </MDTypography>
                  )}
                </MDBox>
              </MDBox>
              <MDBox mb={1}>
                <MDButton onClick={() => formSubmit()} variant="gradient" color="info" fullWidth>
                  Reset
                </MDButton>
              </MDBox>
            </MDBox>
          </FormProvider>
        </MDBox>
      </Card>
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={3} mt={3}>
        <MDBox component="img" src={mastercardLogo} alt="Mastercard" sx={{ height: "28px" }} />
        <MDBox component="img" src={visaLogo} alt="Visa" sx={{ height: "28px" }} />
        <MDBox component="img" src={pciLogo} alt="PCI DSS Compliant" sx={{ height: "28px" }} />
        <MDBox component="img" src={botimLogo} alt="Botim" sx={{ height: "28px" }} />
      </MDBox>
      <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} mt={1.5} sx={{ whiteSpace: "nowrap" }}>
        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
          &copy; {new Date().getFullYear()} YouCloud Technologies. All Rights Reserved.
        </MDTypography>
        <MDTypography variant="caption" color="text" sx={{ fontSize: "0.65rem" }}>
          Version {version}
        </MDTypography>
      </MDBox>
    </BasicLayout>
  );
}

export default ResetWithOtp;
